import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

/**
 * The card is the workhorse surface in Builder.
 *
 * April-2026 design pass
 * ──────────────────────
 * Cards now default to the elev-1 scale (tight edge + soft contact) and
 * expose an opt-in `interactive` prop that layers the hover-raise (.lift)
 * + accent halo (.glow-brand). Non-interactive cards stay flat — only
 * surfaces that the user will *click* earn the lift, otherwise the page
 * feels jittery under the cursor.
 *
 * Tone knobs
 * ──────────
 *   - `tone="plain"` (default) — surface-2 with a single hairline.
 *   - `tone="raised"`         — elev-2, heavier shadow, used for modals
 *                               and hero KPIs that need to read above the
 *                               rest of the page.
 *   - `tone="glass"`          — surface-2/60 + backdrop-blur, for hero
 *                               overlays on aurora backdrops (login,
 *                               owner portal splash).
 */
type Tone = 'plain' | 'raised' | 'glass';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: Tone;
  /** Add hover-raise + accent halo. Only use on actually-clickable cards. */
  interactive?: boolean;
}

const toneClass: Record<Tone, string> = {
  plain: 'bg-surface-2 border border-line elev-1',
  raised: 'bg-surface-2 border border-line elev-2',
  glass:
    'bg-surface-2/70 backdrop-blur-xl border border-line/80 elev-2',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, tone = 'plain', interactive, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'relative overflow-hidden rounded-2xl',
        toneClass[tone],
        interactive && 'lift glow-brand',
        interactive &&
          'hover:border-line-strong dark:hover:border-line-strong',
        className,
      )}
      {...rest}
    />
  );
});

export const CardHeader = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-5 pt-5 pb-3', className)} {...rest} />
);

export const CardBody = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-5 py-4', className)} {...rest} />
);

export const CardFooter = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('px-5 py-4 border-t border-line bg-surface-3/60', className)}
    {...rest}
  />
);
